import { SERVICE_PAGE_CONFIGS } from '../components/servicePages/serviceConfigs.js';
import { getLocationConfigBySlug } from '../components/locationPages/locationConfigs.js';
import { getSpecialGuideBySlug } from '../data/specialServiceGuidesRegistry.js';
import { BLOG_POST_LINK_CORPUS } from '../data/blogPostLinkCorpus.js';
import { getExtraKeywordPageByPath } from './extraKeywordLandingPages.js';
import {
  buildHomepageSeoCopy,
  defaultHomepageFormatMoney,
  HOMEPAGE_SEO_DESCRIPTION,
  HOMEPAGE_SEO_TITLE,
} from './homepageSeo.js';
import { buildOrgHomepageSeoDefault } from './orgMarketingSeoDefaults.js';
import {
  getHubPageDefaultSeo,
  hasHubPageSeoOverride,
  HUB_PAGE_SEO_LABELS,
  resolveHubPageSeoCopy,
} from './marketingHubSeo.js';
import {
  hasLandingPageSeoOverride,
  LANDING_PAGE_SEO_EXAMPLES,
  normalizeMarketingPath,
  resolveLandingPageSeoCopy,
} from './marketingLandingSeo.js';
import type { PublicMarketingConfig } from './publicMarketingConfig.js';

/** Static special landings (same corpus as edge crawler). */
const SPECIAL_LANDING_CORPUS: Record<string, { title: string; description: string }> = {
  '/bed-bug-treatment-kochi': {
    title: 'Bed Bug Treatment Kochi | Syngenta Acrenzel Professional Service | Tools & Bubbles',
    description:
      'Expert bed bug treatment in Kochi & Ernakulam — 3-step inspection, Syngenta Acrenzel WDG spray, verification. Child & family safety guide, re-entry times, AMC options. Book online for instant confirmation.',
  },
  '/ulv-fogging-disinfection-kochi': {
    title: 'ULV Fogging Disinfection Kochi | 3M P2 Anti-Viral | Tools & Bubbles',
    description:
      'ULV fogging in Kochi & Ernakulam: 3M P2 hospital-grade BKC disinfectant. Anti-viral, anti-bacterial, anti-fungal. Homes, offices, clinics, schools. Safety guide, re-entry times. Book online.',
  },
};

export type MarketingSeoPreviewBranding = {
  companyName: string;
  phone?: string;
  email?: string;
  /** Tenant marketing site origin for canonical / SERP display URL preview. */
  websiteUrl?: string;
  /** Tenant logo used as the SERP/social thumbnail when no custom og:image is set. */
  logoUrl?: string;
  /** Org tagline, used for honest org-derived homepage defaults. */
  tagline?: string;
  /** Org city, used for honest org-derived homepage defaults. */
  city?: string;
  /** Org region/state, used for honest org-derived homepage defaults. */
  region?: string;
  /**
   * When true, an un-customised homepage shows copy derived from THIS org's
   * details instead of the legacy first-party (Tools & Bubbles) corpus. Set for
   * SaaS tenants so they never inherit another brand's claims.
   */
  useOrgHomepageDefault?: boolean;
};

export type MarketingSeoPreviewSource = 'homepage-override' | 'hub-override' | 'landing-override' | 'corpus';

export type MarketingSeoPreviewResult = {
  path: string;
  title: string;
  description: string;
  source: MarketingSeoPreviewSource;
};

export type MarketingSeoPreviewPathOption = {
  path: string;
  label: string;
  group: 'core' | 'hub' | 'landing';
};

export function listMarketingSeoPreviewPaths(config: PublicMarketingConfig): MarketingSeoPreviewPathOption[] {
  const options: MarketingSeoPreviewPathOption[] = [{ path: '/', label: 'Homepage', group: 'core' }];

  for (const [path, label] of Object.entries(HUB_PAGE_SEO_LABELS)) {
    options.push({ path, label, group: 'hub' });
  }

  const landingPaths = new Set<string>(LANDING_PAGE_SEO_EXAMPLES.map((example) => example.path));
  for (const path of Object.keys(config.landingPageSeo)) {
    landingPaths.add(path);
  }
  for (const page of config.extraKeywordPages ?? []) {
    if (page.enabled && page.path) landingPaths.add(page.path);
  }

  for (const path of landingPaths) {
    const example = LANDING_PAGE_SEO_EXAMPLES.find((item) => item.path === path);
    options.push({
      path,
      label: example?.label ?? `Landing ${path}`,
      group: 'landing',
    });
  }

  return options;
}

function getDefaultCorpusForPath(path: string): { title: string; description: string } | null {
  if (path === '/') {
    return { title: HOMEPAGE_SEO_TITLE, description: HOMEPAGE_SEO_DESCRIPTION };
  }

  const hubDefaults = getHubPageDefaultSeo(path);
  if (hubDefaults.title || hubDefaults.description) {
    return hubDefaults;
  }

  const special = SPECIAL_LANDING_CORPUS[path];
  if (special) return special;

  if (path.startsWith('/guides/')) {
    const slug = path.slice('/guides/'.length);
    const guide = getSpecialGuideBySlug(slug);
    if (guide) {
      return { title: guide.seo.title, description: guide.seo.description };
    }
  }

  const slugKey = path.replace(/^\//, '');
  const serviceCfg = SERVICE_PAGE_CONFIGS[slugKey];
  if (serviceCfg) {
    return {
      title: serviceCfg.seo.metaTitle,
      description: serviceCfg.seo.metaDescription,
    };
  }

  const loc = getLocationConfigBySlug(path);
  if (loc) {
    return {
      title:
        loc.seoMetaTitle ??
        `#1 Cleaning Service ${loc.locationName} | Deep Cleaning Near Me | Same-Day Service`,
      description:
        loc.seoMetaDescription ??
        `⭐ #1 Rated Cleaning Service in ${loc.locationName}, Kochi! Same-day deep cleaning, home cleaning, sofa cleaning, office cleaning near me. Professional team, 4.9★ rating, 5000+ happy customers. Serving ${loc.locationName} and nearby areas. Book online now!`,
    };
  }

  if (path.startsWith('/blog/')) {
    const slug = path.slice('/blog/'.length);
    const post = BLOG_POST_LINK_CORPUS.find((entry) => entry.slug === slug);
    if (post) {
      return { title: post.title, description: post.scanText.slice(0, 160) };
    }
  }

  return null;
}

export function resolveMarketingSeoPreviewForPath(
  pathInput: string,
  config: PublicMarketingConfig,
  branding: MarketingSeoPreviewBranding,
  formatMoney: (amount: number) => string = defaultHomepageFormatMoney,
): MarketingSeoPreviewResult | null {
  const path = pathInput.trim() === '/' ? '/' : normalizeMarketingPath(pathInput);
  if (!path) return null;

  const brandingOptions = {
    companyName: branding.companyName,
    phone: branding.phone,
    email: branding.email,
  };

  if (path === '/') {
    const hasOverride = Boolean(config.homepageSeoTitle.trim() || config.homepageSeoDescription.trim());
    // Tenants without an override get honest copy derived from their own
    // details — never the first-party (T&B) corpus with its specific claims.
    if (!hasOverride && branding.useOrgHomepageDefault && branding.companyName.trim()) {
      const orgDefault = buildOrgHomepageSeoDefault({
        companyName: branding.companyName,
        tagline: branding.tagline,
        city: branding.city,
        region: branding.region,
      });
      return { path, title: orgDefault.title, description: orgDefault.description, source: 'corpus' };
    }
    const { title, description } = buildHomepageSeoCopy(formatMoney, brandingOptions, undefined, {
      title: config.homepageSeoTitle,
      description: config.homepageSeoDescription,
    });
    return {
      path,
      title,
      description,
      source: hasOverride ? 'homepage-override' : 'corpus',
    };
  }

  if (getHubPageDefaultSeo(path).title || HUB_PAGE_SEO_LABELS[path]) {
    const defaults = getHubPageDefaultSeo(path);
    const { title, description } = resolveHubPageSeoCopy(path, defaults, brandingOptions, config.hubPageSeo);
    return {
      path,
      title,
      description,
      source: hasHubPageSeoOverride(path, config.hubPageSeo) ? 'hub-override' : 'corpus',
    };
  }

  const defaults =
    getDefaultCorpusForPath(path) ??
    (() => {
      const extra = getExtraKeywordPageByPath(config.extraKeywordPages, path);
      return extra ? { title: extra.title || extra.h1, description: extra.description || extra.intro } : null;
    })();
  if (!defaults && !hasLandingPageSeoOverride(path, config.landingPageSeo)) {
    return null;
  }

  const corpus = defaults ?? { title: '', description: '' };
  const { title, description } = resolveLandingPageSeoCopy(
    path,
    corpus,
    brandingOptions,
    config.landingPageSeo,
  );

  return {
    path,
    title,
    description,
    source: hasLandingPageSeoOverride(path, config.landingPageSeo) ? 'landing-override' : 'corpus',
  };
}

/** Merge imported landing SEO map into existing config (org/platform safe). */
export function mergeImportedLandingPageSeo(
  current: PublicMarketingConfig['landingPageSeo'],
  imported: unknown,
): PublicMarketingConfig['landingPageSeo'] {
  if (!imported || typeof imported !== 'object') {
    throw new Error('Import must be a JSON object keyed by path');
  }

  const merged = { ...current };
  for (const [rawPath, rawEntry] of Object.entries(imported as Record<string, unknown>)) {
    const path = normalizeMarketingPath(rawPath);
    if (!path || path === '/') continue;
    if (!rawEntry || typeof rawEntry !== 'object') continue;
    const entry = rawEntry as Record<string, unknown>;
    const title = String(entry.title ?? '').trim();
    const description = String(entry.description ?? '').trim();
    if (!title && !description) continue;
    merged[path] = {
      title: title || merged[path]?.title || '',
      description: description || merged[path]?.description || '',
    };
  }
  return merged;
}
